import { IconArrowLeft } from '@tabler/icons-react';
import type { PersonStanding, StaffPositions } from '../../shared/contracts';
import type { SectionIssues } from '../issues';
import { FtloLogo } from './FtloLogo';
import { IssuesBadge } from './IssuesBadge';
import { Link } from './Link';
import { LogoutControl } from './LogoutControl';
import { StaffPositionBadges } from './StaffPositionBadges';
import { StandingBadge } from './StandingBadge';

const NO_ISSUES: SectionIssues = {};
const NO_POSITIONS: StaffPositions = [];

export function SiteHeader({
  title,
  back,
  standing,
  staffPositions = NO_POSITIONS,
  issues = NO_ISSUES,
  onHome,
  onLogout,
  impersonating,
  onStopImpersonating,
}: {
  title?: string;
  /** Replaces the logo with a back arrow on a phone. */
  back?: { href: string; label: string };
  standing?: PersonStanding;
  staffPositions?: StaffPositions;
  issues?: SectionIssues;
  onHome?: () => void;
  onLogout?: () => void;
  impersonating?: { name: string } | null;
  onStopImpersonating?: () => void;
}) {
  return (
    <header className="bg-ink text-white print:hidden">
      <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 px-4 py-3 sm:grid-cols-[minmax(max-content,1fr)_minmax(0,auto)_minmax(max-content,1fr)] sm:gap-4 sm:px-8">
        <span className="justify-self-start">
          {back ? (
            <Link
              href={back.href}
              aria-label={back.label}
              className="focus-visible:outline-brand portal-press flex h-8 w-8 items-center justify-center rounded-sm hover:opacity-75 focus-visible:outline-2 focus-visible:outline-offset-4 sm:hidden"
            >
              <IconArrowLeft size={24} aria-hidden />
            </Link>
          ) : null}
          <span className={back ? 'hidden sm:block' : undefined}>
            {onHome ? (
              <button
                type="button"
                onClick={onHome}
                aria-label="Home"
                className="focus-visible:outline-brand portal-press block cursor-pointer rounded-sm hover:opacity-75 focus-visible:outline-2 focus-visible:outline-offset-4"
              >
                <FtloLogo className="h-8 w-auto sm:h-10" />
              </button>
            ) : (
              <span>
                <FtloLogo className="h-8 w-auto sm:h-10" />
                <span className="sr-only">FTLO</span>
              </span>
            )}
          </span>
        </span>
        <h1 className="font-display truncate text-center text-lg font-bold tracking-wide uppercase">
          {title ?? <span className="sr-only">FTLO Member Portal</span>}
        </h1>
        <span className="flex min-w-0 items-center gap-2 justify-self-end">
          {standing ? <StandingBadge standing={standing} /> : null}
          <StaffPositionBadges positions={staffPositions} />
          <IssuesBadge issues={issues} />
          <LogoutControl
            onLogout={onLogout}
            impersonating={impersonating}
            onStopImpersonating={onStopImpersonating}
          />
        </span>
      </div>
    </header>
  );
}
